"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import { Clock } from "lucide-react";
import { useGetServicesQuery } from "@/redux/features/divisions/divisionsApi";
import { TService } from "@/types";
import { ServiceTheme } from "./BookingStepper";

export interface SupportOption {
  id: string;
  name: string;
  description: string;
  duration: string;
  price: number;
  image?: string;
  serviceId?: string; // Backend service ID
  paymentType?: string; // FULL_UPFRONT or SPLIT_50_50
}

interface SupportOptionsProps {
  title?: string;
  subtitle?: string;
  options?: SupportOption[];
  defaultSelectedId?: string;
  onOptionSelect?: (option: SupportOption) => void;
  divisionId?: string; // Backend division ID to filter services
  serviceTheme?: ServiceTheme;
  serviceName?: string;
}

const themeConfig: Record<
  ServiceTheme,
  {
    borderClass: string;
    hoverBorderClass: string;
    selectedBorderClass: string;
    selectedRingClass: string;
    badgeBgClass: string;
    badgeTextClass: string;
  }
> = {
  counseling: {
    borderClass: "border-[#896deb]",
    hoverBorderClass: "hover:border-[#7a5de0]",
    selectedBorderClass: "border-[#896deb]",
    selectedRingClass: "ring-[#896deb]/30",
    badgeBgClass: "bg-[#896deb]/10",
    badgeTextClass: "text-[#896deb]",
  },
  lifeCoaching: {
    borderClass: "border-[#DDA500]",
    hoverBorderClass: "hover:border-[#c99400]",
    selectedBorderClass: "border-[#DDA500]",
    selectedRingClass: "ring-[#DDA500]/30",
    badgeBgClass: "bg-[#DDA500]/10",
    badgeTextClass: "text-[#DDA500]",
  },
  occupationalTherapy: {
    borderClass: "border-[#C01610]",
    hoverBorderClass: "hover:border-[#a01210]",
    selectedBorderClass: "border-[#C01610]",
    selectedRingClass: "ring-[#C01610]/30",
    badgeBgClass: "bg-[#C01610]/10",
    badgeTextClass: "text-[#C01610]",
  },
  psychology: {
    borderClass: "border-[#C01610]",
    hoverBorderClass: "hover:border-[#a01210]",
    selectedBorderClass: "border-[#C01610]",
    selectedRingClass: "ring-[#C01610]/30",
    badgeBgClass: "bg-[#C01610]/10",
    badgeTextClass: "text-[#C01610]",
  },
};

const defaultTheme = {
  borderClass: "border-v3-primary",
  hoverBorderClass: "hover:border-[#0059e6]",
  selectedBorderClass: "border-v3-primary",
  selectedRingClass: "ring-v3-primary/30",
  badgeBgClass: "bg-v3-primary/10",
  badgeTextClass: "text-v3-primary",
};

// Convert backend service to frontend support option
const serviceToSupportOption = (service: TService): SupportOption => {
  return {
    id: service.id,
    name: service.name,
    description:
      service.client_end_description ||
      service.description ||
      "One-on-one session focused on your specific needs and personal goals.",
    duration: "60 min", // Default duration, could be added to service model
    price: Number(service.base_amount) || 0,
    image: service.photo,
    serviceId: service.id,
    paymentType: service.payment_type,
  };
};

const defaultOptions: SupportOption[] = [
  {
    id: "individual",
    name: "Individual Session",
    description:
      "One-on-one session focused on your specific needs and personal goals.",
    duration: "60 min",
    price: 130,
    image: "/images/v3/booking/support/support-option-1.png",
  },
  {
    id: "couples",
    name: "Couples Session",
    description:
      "One-on-one session focused on your specific needs and personal goals.",
    duration: "60 min",
    price: 160,
    image: "/images/v3/booking/support/support-option-2.png",
  },
  {
    id: "family",
    name: "Family Session",
    description:
      "One-on-one session focused on your specific needs and personal goals.",
    duration: "60 min",
    price: 190,
    image: "/images/v3/booking/support/support-option-3.png",
  },
];

const SupportOptionCard = ({
  option,
  index,
  isSelected,
  onSelect,
  serviceTheme,
}: {
  option: SupportOption;
  index: number;
  isSelected: boolean;
  onSelect: () => void;
  serviceTheme?: ServiceTheme;
}) => {
  const fallbackIndex = (index % 3) + 1;
  const imageSrc = option.image || `/images/v3/booking/support/support-option-${fallbackIndex}.png`;
  const colors = serviceTheme ? themeConfig[serviceTheme] : defaultTheme;

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={isSelected}
      aria-label={`Select ${option.name} - $${option.price}`}
      className={`
        group relative aspect-[364/476] w-full overflow-hidden rounded-[10px] border bg-[#e3e3e3]
        cursor-pointer transition-all duration-300 ease-out text-left
        focus:outline-none focus-visible:ring-2 focus-visible:ring-v3-primary focus-visible:ring-offset-2
        sm:min-w-[280px] sm:max-w-[364px] sm:flex-1 h-[200px] sm:h-auto
        ${colors.borderClass}
        ${isSelected
          ? `${colors.selectedBorderClass} ring-8 ${colors.selectedRingClass} shadow-lg`
          : `${colors.hoverBorderClass} hover:shadow-lg`
        }
      `}
    >
      {/* Background Image */}
      <div className="absolute inset-0 overflow-hidden">
        <Image
          src={imageSrc}
          alt=""
          fill
          className="object-cover transition-transform duration-700 ease-out group-hover:scale-110"
          sizes="493px"
        />
      </div>

      {/* Selection Indicator - Checkmark */}
      <div
        className={`absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-white/30 backdrop-blur-sm transition-all duration-1000 md:h-10 md:w-10 ${
          isSelected
            ? "scale-100 opacity-100 animate-[pulse_6s_ease-in-out_infinite]"
            : "scale-75 opacity-0"
        }`}
      >
        <svg
          className="h-4 w-4 text-white md:h-5 md:w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={3}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M5 13l4 4L19 7"
          />
        </svg>
      </div>

      {/* Gradient Overlay with Blur - positioned at bottom */}
      <div
        className="absolute -bottom-px -left-px flex w-full flex-col gap-3 px-4 pb-4 pt-[10px] md:gap-5 md:px-[30px] md:pb-[30px] md:pt-[85px]"
        style={{
          background:
            "linear-gradient(rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.85) 50%, rgb(255, 255, 255) 60%)",
          backdropFilter: "blur(2px)",
          WebkitBackdropFilter: "blur(2px)",
          maskImage: "linear-gradient(to bottom, transparent 0%, black 25%)",
          WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, black 25%)",
        }}
      >
        {/* Content */}
        <div className="flex w-full flex-col items-start">
          <div className="p-1.5 md:p-2.5">
            <h3 className="font-onest text-lg font-semibold leading-none text-black md:text-2xl">
              {option.name}
            </h3>
          </div>
          <div className="p-1.5 md:p-2.5">
            <p className="font-onest text-sm leading-tight text-black md:text-base md:leading-none">
              {option.description}
            </p>
          </div>
        </div>

        {/* Duration & Price */}
        <div className="flex w-full items-center justify-between">
          {/* Duration Badge */}
          <div className={`flex items-center rounded-[55px] border ${colors.borderClass} ${colors.badgeBgClass}`}>
            <div className="-mr-px flex size-9 items-center justify-center rounded-full bg-white p-2 md:p-3">
              <Clock className={`size-5 md:size-6 ${colors.badgeTextClass}`} aria-hidden="true" />
            </div>
            <div className="flex h-9 items-center px-2 md:p-2.5">
              <span className={`font-onest text-base font-bold ${colors.badgeTextClass}`}>
                {option.duration}
              </span>
            </div>
          </div>

          {/* Price */}
          <span className="font-onest text-2xl font-bold leading-none text-black">
            ${option.price}
          </span>
        </div>
      </div>
    </button>
  );
};

const SupportOptions = ({
  title,
  subtitle = "Choose the counselling support type that best fits your needs, with support for relationships, emotions, work and personal wellbeing.",
  options: propOptions,
  defaultSelectedId,
  onOptionSelect,
  divisionId,
  serviceTheme,
  serviceName,
}: SupportOptionsProps) => {
  const [selectedId, setSelectedId] = useState<string | null>(
    defaultSelectedId || null
  );
  const resolvedTitle =
    title ||
    `Select Your ${serviceName || "Counselling"} Support Option`;

  // Fetch services from API if divisionId is provided
  const {
    data: servicesResponse,
    isLoading,
    error,
  } = useGetServicesQuery(
    { division_id: divisionId, is_active: true },
    { skip: !divisionId }
  );

  // Convert API services to support options, or use prop options as fallback
  const options: SupportOption[] = (() => {
    if (propOptions && propOptions.length > 0) {
      return propOptions;
    }

    if (servicesResponse?.data) {
      const services = servicesResponse.data as TService[];
      return services
        .filter((s) => s.is_active)
        .sort((a, b) => a.order - b.order)
        .map(serviceToSupportOption);
    }

    return defaultOptions;
  })();

  // Reset selection when options change
  useEffect(() => {
    if (defaultSelectedId) {
      setSelectedId(defaultSelectedId);
    }
  }, [defaultSelectedId, divisionId]);

  const handleSelect = (option: SupportOption) => {
    setSelectedId(option.id);
    onOptionSelect?.(option);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col gap-10">
        <div className="flex flex-col">
          <div className="p-2.5">
            <h2 className="font-crimsonText text-2xl font-bold leading-tight text-v3-secondary sm:text-3xl md:text-[42px] md:leading-none">
              {resolvedTitle}
            </h2>
          </div>
          <div className="w-full max-w-[614px] p-2.5">
            <p className="font-onest text-base font-normal leading-[1.2] text-v3-secondary md:text-lg md:leading-[1.1]">
              {subtitle}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-[21px] lg:grid-cols-3 xl:grid-cols-4">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="aspect-[493/602] animate-pulse rounded-[10px] bg-gray-200"
            />
          ))}
        </div>
      </div>
    );
  }

  // Error state - fall back to default options
  if (error) {
    console.error("Failed to fetch services:", error);
  }

  return (
    <div className="flex flex-col gap-10">
      {/* Header */}
      <div className="flex flex-col">
        <div className="p-2.5">
          <h2 className="font-crimsonText text-2xl font-bold leading-tight text-v3-secondary sm:text-3xl md:text-[42px] md:leading-none">
            {resolvedTitle}
          </h2>
        </div>
        <div className="w-full max-w-[614px] p-2.5">
          <p className="font-onest text-base font-normal leading-[1.2] text-v3-secondary md:text-lg md:leading-[1.1]">
            {subtitle}
          </p>
        </div>
      </div>

      {/* Option Cards */}
      <div
        className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-[21px] lg:grid-cols-3 xl:grid-cols-4"
        role="radiogroup"
        aria-label="Support options"
      >
        {options.map((option, index) => (
          <SupportOptionCard
            key={option.id}
            option={option}
            index={index}
            isSelected={selectedId === option.id}
            serviceTheme={serviceTheme}
            onSelect={() => handleSelect(option)}
          />
        ))}
      </div>
    </div>
  );
};

export default SupportOptions;
